import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useState } from 'react';

import { ACCENT_SX, BulkRunProps } from './dialogShared';
import { LocationNodeData } from '../../../@types/live-search.type';
import { BulkRunStatus } from '../../../components/molecules/BulkRunStatus';
import { useSearchAllocationNodes } from '../../../queries/live-search-query';
import { displayNodeTypeName } from '../../../utils/node-type';

export const AssignDialog: React.FC<
  {
    open: boolean;
    isBulk: boolean;
    count: number;
    submitting: boolean;
    lastError: string | null;
    onClose: () => void;
    onSubmit: (nodeId: number, typeCode: string) => Promise<void>;
  } & BulkRunProps
> = ({ open, isBulk, count, submitting, lastError, onClose, onSubmit, progress, failures }) => {
  const searchMutation = useSearchAllocationNodes();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<LocationNodeData[]>([]);
  const [selectedNode, setSelectedNode] = useState<LocationNodeData | null>(null);

  useEffect(() => {
    if (open) {
      setInputValue('');
      setOptions([]);
      setSelectedNode(null);
    }
  }, [open]);

  useEffect(() => {
    if (inputValue.length < 2) {
      setOptions([]);
      return;
    }
    // A slower earlier request must not overwrite a newer one's results.
    let active = true;
    const timer = setTimeout(() => {
      searchMutation
        .mutateAsync(inputValue)
        .then((nodes) => {
          if (active) setOptions(nodes);
        })
        .catch(() => {
          if (active) setOptions([]);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue]);

  const handleSubmit = useCallback(() => {
    if (selectedNode) onSubmit(selectedNode.id, 'primary');
  }, [selectedNode, onSubmit]);

  const title = isBulk ? `Assign Technician (${count} jobs)` : 'Assign Technician';

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-label={title}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Autocomplete
          size="small"
          options={options}
          getOptionLabel={(opt) => opt.name}
          filterOptions={(x) => x}
          inputValue={inputValue}
          onInputChange={(_, val) => setInputValue(val)}
          onChange={(_, node) => setSelectedNode(node)}
          loading={searchMutation.isPending}
          sx={{ mt: 1 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search technician"
              placeholder="Type to search..."
              aria-label="Search technician"
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={`${option.id}-${option.code}`}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{option.name}</Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                  {displayNodeTypeName(option.nodeTypeName)} &middot; {option.code}
                </Typography>
              </Box>
            </li>
          )}
        />
        <BulkRunStatus
          progress={progress}
          submitting={submitting}
          failures={failures}
          error={lastError}
          verb="Assigning"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} aria-label="Cancel assign">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedNode || submitting}
          sx={ACCENT_SX}
          aria-label="Submit technician assignment"
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
